import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { ResizableImage } from "./ResizableImage";
import Link from "@tiptap/extension-link";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { JUSTIFY_MAX_WORDS } from "../lib/letterPdf";
import {
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify, IndentIncrease, IndentDecrease,
  List, ListOrdered, Table as TableIcon, Undo, Redo, Minus, Plus, Baseline, Highlighter, PaintBucket,
  TextSelect, Copy, ClipboardPaste, SpellCheck, ImagePlus, Link as LinkIcon, Replace,
  BetweenVerticalStart, BetweenVerticalEnd, BetweenHorizontalStart, BetweenHorizontalEnd,
  Combine, Split, Trash2, Grid3x3, LayoutGrid, Building2,
  AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd, AlertTriangle,
} from "lucide-react";

// Attributi bordo per le celle: consentono di personalizzare spessore/colore/stile del bordo
// tramite stile inline (ha priorità sul CSS, così si riflette in editor, anteprima e PDF).
const cellBorderAttrs = {
  borderWidth: {
    default: null,
    parseHTML: (el) => el.style.borderWidth || null,
    renderHTML: (a) => (a.borderWidth ? { style: `border-width:${a.borderWidth}` } : {}),
  },
  borderStyle: {
    default: null,
    parseHTML: (el) => el.style.borderStyle || null,
    renderHTML: (a) => (a.borderStyle ? { style: `border-style:${a.borderStyle}` } : {}),
  },
  borderColor: {
    default: null,
    parseHTML: (el) => el.style.borderColor || null,
    renderHTML: (a) => (a.borderColor ? { style: `border-color:${a.borderColor}` } : {}),
  },
  backgroundColor: {
    default: null,
    parseHTML: (el) => el.style.backgroundColor || null,
    renderHTML: (a) => (a.backgroundColor ? { style: `background-color:${a.backgroundColor}` } : {}),
  },
  verticalAlign: {
    default: null,
    parseHTML: (el) => el.style.verticalAlign || null,
    renderHTML: (a) => (a.verticalAlign ? { style: `vertical-align:${a.verticalAlign}` } : {}),
  },
};

const PAGE_W = 210; // mm
const PAGE_H = 297; // mm
const MM_PER_PX = 25.4 / 96;
const DEFAULT_MARGINS = { top: 20, right: 15, bottom: 15, left: 20 };
// Cache dei margini di stampa dello studio (Impostazioni) per la sessione corrente.
let _cachedPrintMargins;
export function invalidatePrintMarginsCache() { _cachedPrintMargins = undefined; }
// True se un editor aperto ha contenuto oltre la prima pagina A4 (usato per l'avviso al salvataggio).
export const editorOverflowsA4 = () => !!document.querySelector('[data-testid="rte-a4-sheet"][data-overflow="1"]');
export const OVERFLOW_MSG = "Attenzione: il contenuto supera la prima pagina A4";
const DEFAULT_LINE_HEIGHT = "1.15";
export const LINE_HEIGHTS = [
  { l: "0.5", v: "0.5" }, { l: "1.0", v: "1" }, { l: "1.15", v: "1.15" }, { l: "1.5", v: "1.5" },
  { l: "2.0", v: "2" }, { l: "2.5", v: "2.5" }, { l: "3.0", v: "3" },
];
export const FONT_SIZES = ["10px", "11px", "12px", "13px", "14px", "16px", "18px", "20px", "24px"];
export const FONT_FAMILIES = [
  { l: "Arial", v: "Arial, Helvetica, sans-serif" },
  { l: "Times New Roman", v: "'Times New Roman', Times, serif" },
  { l: "Courier New", v: "'Courier New', Courier, monospace" },
  { l: "Georgia", v: "Georgia, 'Times New Roman', serif" },
  { l: "Verdana", v: "Verdana, Geneva, sans-serif" },
  { l: "Calibri", v: "Calibri, 'Segoe UI', sans-serif" },
  { l: "Helvetica", v: "Helvetica, Arial, sans-serif" },
];
const WIDE_COLORS = ["#000000", "#374151", "#6b7280", "#9ca3af", "#d1d5db", "#f3f4f6", "#ffffff", "#ef4444", "#dc2626", "#991b1b", "#f97316", "#ea580c", "#f59e0b", "#facc15", "#fde047", "#22c55e", "#16a34a", "#15803d", "#14b8a6", "#06b6d4", "#3b82f6", "#2563eb", "#1e40af", "#6366f1", "#7c3aed", "#9333ea", "#ec4899", "#db2777", "#92400e", "#b45309", "#78350f"];
const TEXT_COLORS = WIDE_COLORS;
const HIGHLIGHT_COLORS = ["#fef08a", "#fde68a", "#bbf7d0", "#a5f3fc", "#bfdbfe", "#ddd6fe", "#fbcfe8", "#fecaca", "#fed7aa", "#e5e7eb"];

export const LineHeight = Extension.create({
  name: "lineHeight",
  addOptions() { return { types: ["paragraph", "heading"], defaultHeight: DEFAULT_LINE_HEIGHT }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        lineHeight: {
          default: this.options.defaultHeight,
          parseHTML: (el) => el.style.lineHeight || this.options.defaultHeight,
          renderHTML: (attrs) => (attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {}),
        },
      },
    }];
  },
});

const PARA_SPACINGS = [
  { v: "0", l: "Nessuno" },
  { v: "0.3em", l: "Piccolo" },
  { v: "0.6em", l: "Normale" },
  { v: "1em", l: "Grande" },
  { v: "1.5em", l: "Extra" },
];

// Spazio dopo il paragrafo (margin-bottom) impostabile per paragrafo, indipendente dall'interlinea.
// Il default dei <p> è 0 (CSS): la spaziatura tra paragrafi è controllata solo da questo attributo
// o dall'interlinea. Lo stile inline viene preservato anche nel PDF (html2canvas).
// Rientro sinistro del paragrafo (margin-left in cm): vale per TUTTE le righe, anche con testo
// giustificato (le righe decorate pgp-jl-* sono blocchi interni al paragrafo e ne ereditano il rientro).
export const INDENT_STEP_CM = 0.5;
export const INDENT_MAX_CM = 8;
const FIRST_INDENTS = [0, 0.5, 1, 1.25, 1.5, 2, 2.5, 3];
const ParagraphIndent = Extension.create({
  name: "paragraphIndent",
  addOptions() { return { types: ["paragraph", "heading"] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        indentLeft: {
          default: null,
          parseHTML: (el) => {
            const m = (el.style.marginLeft || "").match(/^([\d.]+)cm$/);
            return m && parseFloat(m[1]) > 0 ? parseFloat(m[1]) : null;
          },
          renderHTML: (attrs) => (attrs.indentLeft ? { style: `margin-left: ${attrs.indentLeft}cm` } : {}),
        },
        firstIndent: {
          default: null,
          parseHTML: (el) => {
            const m = (el.style.textIndent || "").match(/^([\d.]+)cm$/);
            return m && parseFloat(m[1]) > 0 ? parseFloat(m[1]) : null;
          },
          renderHTML: (attrs) => (attrs.firstIndent ? { style: `text-indent: ${attrs.firstIndent}cm` } : {}),
        },
      },
    }];
  },
  addCommands() {
    return {
      changeIndent: (delta) => ({ state, tr, dispatch }) => {
        const { from, to } = state.selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!this.options.types.includes(node.type.name)) return;
          const cur = node.attrs.indentLeft || 0;
          const next = Math.min(INDENT_MAX_CM, Math.max(0, Math.round((cur + delta) * 10) / 10));
          if (next !== cur) { tr.setNodeMarkup(pos, undefined, { ...node.attrs, indentLeft: next || null }); changed = true; }
        });
        if (changed && dispatch) dispatch(tr);
        return changed;
      },
      setFirstIndent: (cm) => ({ state, tr, dispatch }) => {
        const { from, to } = state.selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!this.options.types.includes(node.type.name)) return;
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, firstIndent: cm > 0 ? cm : null }); changed = true;
        });
        if (changed && dispatch) dispatch(tr);
        return changed;
      },
    };
  },
});

const ParagraphSpacing = Extension.create({
  name: "paragraphSpacing",
  addOptions() { return { types: ["paragraph"] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        marginBottom: {
          default: null,
          parseHTML: (el) => el.style.marginBottom || null,
          renderHTML: (attrs) => (attrs.marginBottom ? { style: `margin-bottom: ${attrs.marginBottom}` } : {}),
        },
      },
    }];
  },
});

export const FontSize = Extension.create({
  name: "fontSize",
  addOptions() { return { types: ["textStyle"] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize || null,
          renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
        },
      },
    }];
  },
});

export const FontFamily = Extension.create({
  name: "fontFamily",
  addOptions() { return { types: ["textStyle"] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontFamily: {
          default: null,
          parseHTML: (el) => el.style.fontFamily || null,
          renderHTML: (attrs) => (attrs.fontFamily ? { style: `font-family: ${attrs.fontFamily}` } : {}),
        },
      },
    }];
  },
});

// Comportamento tipografico standard per il testo giustificato nell'editor live:
// le righe separate da <br> con poche parole (o l'ultima riga) vengono allineate a
// sinistra invece che giustificate, così editor e PDF coincidono. Usa decorazioni
// (non modifica il documento) per rendere ogni riga un blocco con il proprio allineamento.
const JustifyTypography = Extension.create({
  name: "justifyTypography",
  addProseMirrorPlugins() {
    return [new Plugin({
      key: new PluginKey("justifyTypography"),
      props: {
        decorations(state) {
          const decos = [];
          state.doc.descendants((node, pos) => {
            if (!node.isTextblock || node.attrs?.textAlign !== "justify") return;
            let hasBreak = false;
            node.forEach((c) => { if (c.type.name === "hardBreak") hasBreak = true; });
            if (!hasBreak) return;
            const contentStart = pos + 1;
            const contentEnd = pos + 1 + node.content.size;
            let lineStart = contentStart;
            let words = 0;
            const segments = [];
            node.forEach((child, offset) => {
              const childPos = contentStart + offset;
              if (child.type.name === "hardBreak") {
                segments.push({ from: lineStart, to: childPos, words, brPos: childPos });
                lineStart = childPos + child.nodeSize;
                words = 0;
              } else if (child.isText) {
                words += ((child.text || "").trim().match(/\S+/g) || []).length;
              }
            });
            segments.push({ from: lineStart, to: contentEnd, words, brPos: null });
            segments.forEach((seg, idx) => {
              if (seg.to <= seg.from) return; // riga vuota: lascia il <br> per conservare l'altezza
              // Se il segmento contiene testo con marcature (grassetto, colore, dimensione, ecc.),
              // ProseMirror spezzerebbe la decorazione display:block in più blocchi (uno per ogni
              // confine di mark), mandando a capo nel punto sbagliato. In questo caso si lascia il
              // flusso nativo del paragrafo (nessuna decorazione a blocco) mantenendo il <br>
              // visibile per l'a-capo: il grassetto non altera così il flusso del testo giustificato.
              let marked = false;
              state.doc.nodesBetween(seg.from, seg.to, (n) => { if (n.isText && n.marks && n.marks.length) marked = true; });
              if (marked) return;
              const isLast = idx === segments.length - 1;
              const left = isLast || seg.words < JUSTIFY_MAX_WORDS;
              decos.push(Decoration.inline(seg.from, seg.to, { class: left ? "pgp-jl-left" : "pgp-jl-just" }));
              if (seg.brPos != null) decos.push(Decoration.node(seg.brPos, seg.brPos + 1, { class: "pgp-jl-brhide" }));
            });
          });
          return DecorationSet.create(state.doc, decos);
        },
      },
    })];
  },
});

const CustomTableCell = TableCell.extend({
  addAttributes() { return { ...this.parent?.(), ...cellBorderAttrs }; },
});
const CustomTableHeader = TableHeader.extend({
  addAttributes() { return { ...this.parent?.(), ...cellBorderAttrs }; },
});

// Altezza riga tabella salvata come attributo sul <tr> (stile height).
const CustomTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      rowHeight: {
        default: null,
        parseHTML: (el) => {
          const h = parseInt(el.style.height || el.getAttribute("data-row-height") || "", 10);
          return Number.isFinite(h) ? h : null;
        },
        renderHTML: (attrs) => (attrs.rowHeight ? { style: `height:${attrs.rowHeight}px`, "data-row-height": attrs.rowHeight } : {}),
      },
    };
  },
});

// Ridimensionamento dell'altezza delle righe tramite trascinamento del bordo inferiore,
// analogo al resize delle colonne. Nessun accesso cross-origin: solo API ProseMirror/DOM locali.
const ROW_EDGE = 5;
function findRowBottomEdge(view, event) {
  const trs = view.dom.querySelectorAll("tr");
  for (const tr of trs) {
    const r = tr.getBoundingClientRect();
    if (event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.bottom - ROW_EDGE && event.clientY <= r.bottom + ROW_EDGE) return tr;
  }
  return null;
}
function rowPosFromDOM(view, trDOM) {
  try {
    const pos = view.posAtDOM(trDOM, 0);
    const $p = view.state.doc.resolve(pos);
    for (let d = $p.depth; d > 0; d--) { if ($p.node(d).type.name === "tableRow") return $p.before(d); }
  } catch { /* fuori dalla vista */ }
  return null;
}
const RowResize = Extension.create({
  name: "rowResize",
  addProseMirrorPlugins() {
    let drag = null;
    return [new Plugin({
      key: new PluginKey("rowResize"),
      props: {
        handleDOMEvents: {
          mousemove(view, event) {
            if (drag) return false;
            const tr = findRowBottomEdge(view, event);
            view.dom.style.cursor = tr ? "row-resize" : "";
            return false;
          },
          mousedown(view, event) {
            if (event.button !== 0 || drag) return false;
            const trDOM = findRowBottomEdge(view, event);
            if (!trDOM) return false;
            const rowPos = rowPosFromDOM(view, trDOM);
            if (rowPos == null) return false;
            event.preventDefault();
            const rect = trDOM.getBoundingClientRect();
            const scale = trDOM.offsetHeight ? rect.height / trDOM.offsetHeight : 1;
            drag = { trDOM, rowPos, startY: event.clientY, startH: trDOM.offsetHeight || rect.height, scale: scale || 1, cur: null };
            view.dom.style.cursor = "row-resize";
            const onMove = (e) => {
              if (!drag) return;
              const h = Math.max(16, Math.round(drag.startH + (e.clientY - drag.startY) / drag.scale));
              drag.cur = h; drag.trDOM.style.height = `${h}px`;
            };
            const onUp = () => {
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onUp);
              view.dom.style.cursor = "";
              if (drag && drag.cur) {
                const node = view.state.doc.nodeAt(drag.rowPos);
                if (node && node.type.name === "tableRow") {
                  view.dispatch(view.state.tr.setNodeMarkup(drag.rowPos, undefined, { ...node.attrs, rowHeight: drag.cur }));
                }
              }
              drag = null;
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
            return true;
          },
        },
      },
    })];
  },
});

const BORDER_COLORS = WIDE_COLORS;

function TableBorderControl({ editor, onApply }) {
  const [open, setOpen] = useState(false);
  const cur = editor.getAttributes("tableCell");
  const initColor = (typeof cur.borderColor === "string" && cur.borderColor.startsWith("#")) ? cur.borderColor : "#000000";
  const initWidth = (() => { const w = parseInt(cur.borderWidth || "", 10); return Number.isFinite(w) ? w : 1; })();
  const [color, setColor] = useState(initColor);
  const [width, setWidth] = useState(initWidth);

  const apply = (w, c) => {
    if (w <= 0) onApply({ borderStyle: "none", borderWidth: "0px" });
    else onApply({ borderStyle: "solid", borderWidth: `${w}px`, borderColor: c });
  };
  const pickWidth = (w) => { setWidth(w); apply(w, color); };
  const pickColor = (c) => { const w = width <= 0 ? 1 : width; setColor(c); setWidth(w); apply(w, c); };

  return (
    <div className="relative">
      <TblBtn onClick={() => setOpen((o) => !o)} title="Bordo tabella" testId="tbl-border"><Grid3x3 className="h-4 w-4" /></TblBtn>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 left-0 w-52 rounded-md border border-slate-700 bg-[#121824] p-2.5 shadow-xl" data-testid="tbl-border-panel" onMouseDown={(e) => e.preventDefault()}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono mb-1.5">Spessore bordo (px)</div>
            <div className="flex gap-1 mb-2">
              {[0, 1, 2, 3, 4, 5].map((w) => (
                <button key={w} type="button" onClick={() => pickWidth(w)} data-testid={`tbl-border-w-${w}`}
                  className={`h-7 flex-1 rounded text-xs font-semibold border transition-colors ${width === w ? "bg-amber-500 text-[#0a0d14] border-amber-500" : "border-slate-600 text-slate-200 hover:bg-slate-800"}`}>
                  {w === 0 ? "✕" : w}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => pickWidth(0)} data-testid="tbl-border-none" className="w-full h-7 mb-2.5 rounded border border-slate-600 text-xs text-slate-200 hover:bg-slate-800">Nessun bordo</button>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono mb-1.5">Colore bordo</div>
            <div className="grid grid-cols-8 gap-1 mb-2">
              {BORDER_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => pickColor(c)} data-testid={`tbl-border-color-${c.replace("#", "")}`}
                  className="h-5 w-5 rounded border border-slate-600 hover:scale-110 transition-transform" style={{ backgroundColor: c }} title={c} />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <input type="color" value={color} onChange={(e) => pickColor(e.target.value)} data-testid="tbl-border-color-custom" className="h-7 w-9 rounded bg-transparent border border-slate-600 p-0 cursor-pointer" />
              <input type="text" value={color} onChange={(e) => { const v = e.target.value; setColor(v); if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) pickColor(v); }} placeholder="#RRGGBB" data-testid="tbl-border-color-hex" className="h-7 flex-1 rounded bg-slate-900/60 border border-slate-600 px-1.5 text-xs text-slate-100 font-mono focus:outline-none focus:border-amber-500" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const CELL_BG_COLORS = ["#fde68a", "#fecaca", "#bbf7d0", "#bfdbfe", "#e9d5ff", "#fed7aa", "#e5e7eb", "#ffffff"];

function CellBgControl({ editor }) {
  const [open, setOpen] = useState(false);
  const apply = (c) => editor.chain().focus().setCellAttribute("backgroundColor", c).run();
  return (
    <div className="relative">
      <TblBtn onClick={() => setOpen((o) => !o)} title="Colore sfondo celle" testId="tbl-cell-bg"><PaintBucket className="h-4 w-4" /></TblBtn>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 left-0 w-52 rounded-md border border-slate-700 bg-[#121824] p-2.5 shadow-xl" data-testid="tbl-cell-bg-panel" onMouseDown={(e) => e.preventDefault()}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono mb-1.5">Colore sfondo celle</div>
            <div className="grid grid-cols-8 gap-1 mb-2">
              {CELL_BG_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => apply(c)} data-testid={`tbl-cell-bg-${c.replace("#", "")}`}
                  className="h-5 w-5 rounded border border-slate-500 hover:scale-110 transition-transform" style={{ backgroundColor: c }} title={c} />
              ))}
            </div>
            <div className="flex items-center gap-1.5 mb-2">
              <input type="color" defaultValue="#fde68a" onChange={(e) => apply(e.target.value)} data-testid="tbl-cell-bg-custom" className="h-7 w-9 rounded bg-transparent border border-slate-600 p-0 cursor-pointer" />
              <span className="text-xs font-mono text-slate-300">Personalizzato</span>
            </div>
            <button type="button" onClick={() => editor.chain().focus().setCellAttribute("backgroundColor", null).run()} data-testid="tbl-cell-bg-none" className="w-full h-7 rounded border border-slate-600 text-xs text-slate-200 hover:bg-slate-800">Rimuovi sfondo</button>
          </div>
        </>
      )}
    </div>
  );
}

const TabIndent = Extension.create({
  name: "tabIndent",
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (this.editor.can().sinkListItem("listItem")) {
          return this.editor.chain().sinkListItem("listItem").run();
        }
        return this.editor.chain().insertContent("\t").run();
      },
      "Shift-Tab": () => {
        if (this.editor.can().liftListItem("listItem")) {
          return this.editor.chain().liftListItem("listItem").run();
        }
        return true;
      },
    };
  },
});

function Btn({ onClick, active, title, children, testId, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      disabled={disabled}
      className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${
        disabled ? "text-slate-600 opacity-40 cursor-not-allowed" : active ? "bg-amber-500/20 text-amber-400" : "text-slate-400 hover:text-slate-100 hover:bg-slate-700/50"
      }`}
    >
      {children}
    </button>
  );
}

function TblBtn({ onClick, title, children, testId, danger, active }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`h-8 min-w-8 px-1.5 flex items-center justify-center rounded-md transition-colors ${
        active ? "bg-amber-500/20 text-amber-400"
          : danger ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" : "text-slate-300 hover:text-amber-400 hover:bg-slate-700/50"
      }`}
    >
      {children}
    </button>
  );
}

function ColorPopover({ title, testId, icon, colors, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState("#000000");
  const valid = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex);
  const keep = (e) => e.preventDefault();
  return (
    <div className="relative">
      <button type="button" title={title} data-testid={testId} onMouseDown={keep} onClick={() => setOpen((o) => !o)}
        className="h-8 w-8 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700/50 transition-colors">
        {icon}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onMouseDown={keep} onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 left-0 w-56 rounded-md border border-slate-700 bg-[#121824] p-2 shadow-xl" data-testid={`${testId}-panel`} onMouseDown={keep}>
            <div className="grid grid-cols-6 gap-1.5">
              {colors.map((c) => (
                <button key={c} type="button" title={c} onMouseDown={keep} onClick={() => { onPick(c); setOpen(false); }}
                  data-testid={`${testId}-swatch-${c.replace("#", "")}`}
                  className="h-6 w-6 rounded border border-slate-600 transition-transform hover:scale-110" style={{ backgroundColor: c }} />
              ))}
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              <input type="color" value={valid ? hex : "#000000"} onChange={(e) => setHex(e.target.value)} data-testid={`${testId}-hex-input`} className="h-7 w-9 rounded bg-transparent border border-slate-600 p-0 cursor-pointer" />
              <input type="text" value={hex} onChange={(e) => setHex(e.target.value)} placeholder="#RRGGBB" data-testid={`${testId}-hex-text`} className="h-7 flex-1 rounded bg-slate-900/60 border border-slate-600 px-1.5 text-xs text-slate-100 font-mono focus:outline-none focus:border-amber-500" />
              <button type="button" disabled={!valid} onMouseDown={keep} onClick={() => { onPick(hex); setOpen(false); }} data-testid={`${testId}-hex-apply`} className="h-7 px-2 rounded bg-amber-500 disabled:opacity-40 text-[#0a0d14] text-xs font-semibold">OK</button>
            </div>
            <button type="button" onMouseDown={keep} onClick={() => { onClear(); setOpen(false); }} data-testid={`${testId}-clear`} className="mt-2 w-full h-7 rounded border border-slate-600 text-xs text-slate-300 hover:bg-slate-800">Rimuovi</button>
          </div>
        </>
      )}
    </div>
  );
}

function FindReplace({ editor }) {
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState("");
  const [repl, setRepl] = useState("");

  const findNext = () => {
    if (!editor || !find) return;
    const needle = find.toLowerCase();
    const { to } = editor.state.selection;
    const matches = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.isText) {
        const text = node.text.toLowerCase();
        let idx = text.indexOf(needle);
        while (idx !== -1) {
          matches.push({ from: pos + idx, to: pos + idx + find.length });
          idx = text.indexOf(needle, idx + 1);
        }
      }
    });
    if (!matches.length) return;
    const next = matches.find((m) => m.from >= to) || matches[0];
    editor.chain().focus().setTextSelection({ from: next.from, to: next.to }).scrollIntoView().run();
  };

  const replaceAll = () => {
    if (!editor || !find) return;
    const needle = find.toLowerCase();
    const ranges = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.isText) {
        const text = node.text.toLowerCase();
        let idx = text.indexOf(needle);
        while (idx !== -1) {
          ranges.push({ from: pos + idx, to: pos + idx + find.length });
          idx = text.indexOf(needle, idx + 1);
        }
      }
    });
    if (!ranges.length) return;
    let chain = editor.chain().focus();
    for (let i = ranges.length - 1; i >= 0; i--) {
      chain = chain.insertContentAt(ranges[i], repl);
    }
    chain.run();
  };

  return (
    <div className="relative">
      <button type="button" title="Trova e sostituisci" data-testid="rte-find-replace" onClick={() => setOpen((o) => !o)}
        className="h-8 w-8 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700/50 transition-colors">
        <Replace className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 right-0 w-64 rounded-md border border-slate-700 bg-[#121824] p-2.5 shadow-xl space-y-2" data-testid="rte-find-replace-panel">
            <input type="text" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Trova" data-testid="rte-find-input"
              className="w-full h-8 rounded bg-slate-900/60 border border-slate-600 px-2 text-xs text-slate-100 focus:outline-none focus:border-amber-500" />
            <input type="text" value={repl} onChange={(e) => setRepl(e.target.value)} placeholder="Sostituisci con" data-testid="rte-replace-input"
              className="w-full h-8 rounded bg-slate-900/60 border border-slate-600 px-2 text-xs text-slate-100 focus:outline-none focus:border-amber-500" />
            <div className="flex gap-1.5">
              <button type="button" onClick={findNext} data-testid="rte-find-next" className="flex-1 h-7 rounded border border-slate-600 text-xs text-slate-200 hover:bg-slate-800">Trova</button>
              <button type="button" onClick={replaceAll} data-testid="rte-replace-all" className="flex-1 h-7 rounded bg-amber-500 hover:bg-amber-600 text-[#0a0d14] text-xs font-semibold">Sostituisci tutto</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Inserisce un blocco destinatario (Spett.le / ragione sociale / SEDE LEGALE / indirizzo / CAP Comune (Prov))
// copiando font, dimensione e interlinea del primo blocco "Spett.le" esistente, alla posizione del cursore.
// Aggiunge inoltre la PEC nella cella di sinistra, sotto le email, con la stessa formattazione.
function DestinatarioPraticaControl({ editor, companies }) {
  const [open, setOpen] = useState(false);
  const [pecs, setPecs] = useState({});
  const esc = (s) => (s == null ? "" : String(s)).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const up = (s) => (s == null ? "" : String(s)).trim().toUpperCase();

  // Rileva la formattazione (font-size, font-family, grassetto, interlinea) del primo blocco "Spett.le".
  const detectRecipientFmt = () => {
    let fmt = null;
    const doc = editor.state.doc;
    doc.descendants((node, pos) => {
      if (fmt) return false;
      if (node.isText && (node.text || "").toLowerCase().includes("spett")) {
        let fontSize = "", fontFamily = "", bold = false;
        (node.marks || []).forEach((m) => {
          if (m.type.name === "textStyle") {
            fontSize = m.attrs.fontSize || fontSize;
            fontFamily = m.attrs.fontFamily || fontFamily;
          }
          if (m.type.name === "bold") bold = true;
        });
        let lineHeight = "";
        const $p = doc.resolve(pos);
        for (let d = $p.depth; d >= 0; d--) {
          const n = $p.node(d);
          if (n.type.name === "paragraph" || n.type.name === "heading") { lineHeight = (n.attrs && n.attrs.lineHeight) || ""; break; }
        }
        fmt = { fontSize, fontFamily, bold, lineHeight };
        return false;
      }
      return true;
    });
    return fmt || { fontSize: "", fontFamily: "", bold: true, lineHeight: "1.15" };
  };

  const buildRightHtml = (c, fmt, withPec = false) => {
    const rs = up(c.ragione_sociale);
    const via = up([c.indirizzo, c.numero_civico].filter(Boolean).join(", "));
    const prov = up(c.provincia);
    const city = ([String(c.cap || "").trim(), up(c.comune)].filter(Boolean).join(" ") + (prov ? ` (${prov})` : "")).trim();
    const lines = ["Spett.le", rs, "SEDE LEGALE"];
    if (via) lines.push(via);
    if (city) lines.push(city);
    if (withPec && (c.pec || "").trim()) lines.push(`PEC: ${c.pec.trim()}`);
    let inner = lines.map(esc).join("<br>");
    if (fmt.bold) inner = `<strong>${inner}</strong>`;
    const spanParts = [];
    if (fmt.fontSize) spanParts.push(`font-size: ${fmt.fontSize}`);
    if (fmt.fontFamily) spanParts.push(`font-family: ${fmt.fontFamily}`);
    if (spanParts.length) inner = `<span style="${spanParts.join("; ")}">${inner}</span>`;
    const pStyle = fmt.lineHeight ? ` style="line-height: ${fmt.lineHeight}"` : "";
    return `<p${pStyle}>${inner}</p>`;
  };

  // Trova la cella di sinistra (email/PEC) e la posizione dove aggiungere la nuova PEC.
  const findLeftCellPec = () => {
    const doc = editor.state.doc;
    let cell = null;
    doc.descendants((node, pos) => {
      if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
        const t = (node.textContent || "").toLowerCase();
        if ((t.includes("e-mail") || t.includes("p.e.c") || t.includes("@")) && !t.includes("spett")) {
          if (!cell) cell = { node, pos };
        }
      }
    });
    if (!cell) return null;
    const cellStart = cell.pos;
    const cellEnd = cell.pos + cell.node.nodeSize;
    let valueFmt = null, labelFmt = null;
    doc.nodesBetween(cellStart, cellEnd, (node) => {
      if (node.isText) {
        const txt = node.text || "";
        const styleOf = () => {
          let fs = "", ff = "", bold = false;
          (node.marks || []).forEach((m) => {
            if (m.type.name === "textStyle") { fs = m.attrs.fontSize || fs; ff = m.attrs.fontFamily || ff; }
            if (m.type.name === "bold") bold = true;
          });
          return { fs, ff, bold };
        };
        if (txt.includes("@")) valueFmt = styleOf();
        if (txt.toLowerCase().includes("e-mail")) labelFmt = styleOf();
      }
    });
    let lastParaEnd = null;
    cell.node.forEach((child, offset) => {
      if (child.type.name === "paragraph") lastParaEnd = cellStart + 1 + offset + child.nodeSize - 1;
    });
    if (lastParaEnd == null) return null;
    return { insertPos: lastParaEnd, labelFmt, valueFmt };
  };

  const buildLeftPecHtml = (pec, labelFmt, valueFmt) => {
    const lp = [];
    if (labelFmt && labelFmt.fs) lp.push(`font-size: ${labelFmt.fs}`);
    if (labelFmt && labelFmt.ff) lp.push(`font-family: ${labelFmt.ff}`);
    const labelStyle = lp.length ? ` style="${lp.join("; ")}"` : "";
    const vp = [];
    if (valueFmt && valueFmt.fs) vp.push(`font-size: ${valueFmt.fs}`);
    if (valueFmt && valueFmt.ff) vp.push(`font-family: ${valueFmt.ff}`);
    const valStyle = vp.length ? ` style="${vp.join("; ")}"` : "";
    let val = esc(pec);
    if (!valueFmt || valueFmt.bold) val = `<strong>${val}</strong>`;
    return `<br><br><span${labelStyle}>e-mail: </span><span${valStyle}>${val}</span>`;
  };

  const pick = (c) => {
    const pecVal = (pecs[c.label] ?? c.pec ?? "").trim();
    c = { ...c, pec: pecVal };
    const fmt = detectRecipientFmt();
    const pecInRight = !findLeftCellPec();
    editor.chain().focus().insertContent(buildRightHtml(c, fmt, pecInRight)).run();
    const pec = pecInRight ? "" : pecVal;
    if (pec) {
      try {
        const left = findLeftCellPec();
        if (left) editor.chain().insertContentAt(left.insertPos, buildLeftPecHtml(pec, left.labelFmt, left.valueFmt)).run();
      } catch { /* inserimento PEC non bloccante */ }
    }
    setOpen(false);
  };

  return (
    <div className="relative">
      <button type="button" title="Inserisci destinatario pratica" data-testid="rte-insert-destinatario" onClick={() => setOpen((o) => !o)}
        className="h-8 px-2 flex items-center gap-1 rounded-md text-slate-300 hover:text-amber-400 hover:bg-slate-700/50 transition-colors">
        <Building2 className="h-4 w-4" /><span className="text-[11px] font-medium hidden sm:inline">Destinatario</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 right-0 w-72 max-w-[calc(100vw-2rem)] rounded-md border border-slate-700 bg-[#121824] p-2 shadow-xl" data-testid="rte-destinatario-panel">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono px-1 pb-1.5">Compagnie nella pratica</div>
            {(!companies || companies.length === 0) ? (
              <div className="text-xs text-slate-400 px-1 py-2" data-testid="rte-destinatario-empty">Nessuna compagnia presente nella pratica.</div>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto overflow-x-hidden">
                {companies.map((c, i) => (
                  <div key={i} className="rounded border border-slate-700 bg-slate-900/40 hover:border-amber-500/50 transition-colors">
                    <button type="button" onClick={() => pick(c)} data-testid={`rte-destinatario-item-${i}`}
                      className="w-full text-left px-2 py-1.5 hover:bg-slate-800 rounded-t">
                      <div className="text-[10px] text-amber-500 font-mono break-words">{c.label}</div>
                      <div className="text-xs text-slate-100 font-medium break-words">{c.ragione_sociale}</div>
                      {(c.indirizzo || c.comune || c.provincia) ? <div className="text-[11px] text-slate-400 break-words">{[c.indirizzo, c.comune, c.provincia].filter(Boolean).join(" · ")}</div> : null}
                    </button>
                    <div className="flex items-center gap-1.5 px-2 pb-1.5">
                      <span className="text-[10px] font-mono text-sky-400 shrink-0">PEC</span>
                      <input type="email" value={pecs[c.label] ?? c.pec ?? ""} placeholder="Inserisci PEC della compagnia"
                        onChange={(e) => setPecs((m) => ({ ...m, [c.label]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); pick(c); } }}
                        data-testid={`rte-destinatario-pec-${i}`}
                        className="flex-1 min-w-0 h-6 rounded border border-slate-700 bg-slate-950/60 px-1.5 text-[11px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-sky-500" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function RichTextEditor({ value, onChange, onEditorReady, onMarginsChange, initialMargins, letterhead, withHead, destinatariCompagnie, fillHeight }) {
  const [zoom, setZoom] = useState(100);
  const [margins, setMargins] = useState(initialMargins || _cachedPrintMargins || DEFAULT_MARGINS);
  const [spellcheck, setSpellcheck] = useState(false);
  const [tableGuide, setTableGuide] = useState(false);
  const [tableBar, setTableBar] = useState(null); // posizione flottante della barra tabella (viewport coords)
  const [showTableBar, setShowTableBar] = useState(false); // barra tabella: chiusa di default, si apre SOLO col pulsante dedicato
  const tableBarRef = useRef(null);
  const sheetRef = useRef(null);
  const [overPages, setOverPages] = useState(0);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const [canvasMaxH, setCanvasMaxH] = useState(null);
  useEffect(() => {
    if (!fillHeight) return undefined;
    const calc = () => {
      const el = canvasRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const host = el.closest("[role=dialog]");
      const bottomPad = host ? Math.max(8, window.innerHeight - host.getBoundingClientRect().bottom + 10) : 12;
      setCanvasMaxH(Math.max(360, Math.round(window.innerHeight - Math.max(0, top) - bottomPad)));
    };
    const timers = [50, 350, 800].map((ms) => setTimeout(calc, ms));
    window.addEventListener("resize", calc);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(calc) : null;
    const host = canvasRef.current?.closest("[role=dialog]");
    if (ro && host) ro.observe(host);
    if (ro && canvasRef.current?.parentElement) ro.observe(canvasRef.current.parentElement);
    return () => { timers.forEach(clearTimeout); window.removeEventListener("resize", calc); ro?.disconnect(); };
  }, [fillHeight]);
  const [autoFit, setAutoFit] = useState(true);

  // Adatta automaticamente lo zoom per riempire tutta la larghezza orizzontale disponibile con il foglio A4.
  useEffect(() => {
    if (!autoFit) return;
    const el = canvasRef.current;
    if (!el) return;
    const A4_OUTER_PX = 18 + (PAGE_W * 96) / 25.4; // righello verticale (18px) + larghezza A4 @100%
    const fit = () => {
      const avail = el.clientWidth - 48; // padding orizzontale del canvas (24px * 2)
      if (avail <= 0) return;
      setZoom(Math.max(50, Math.min(300, Math.round((avail / A4_OUTER_PX) * 100))));
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [autoFit]);

  // Se non vengono passati margini espliciti (es. lettera/template salvati), usa SEMPRE
  // come default i margini configurati nelle Impostazioni Studio.
  useEffect(() => {
    if (initialMargins) return;
    if (_cachedPrintMargins) { setMargins(_cachedPrintMargins); return; }
    let active = true;
    api.get("/studio/me").then(({ data }) => {
      const pm = data?.settings?.print_margins;
      if (pm && typeof pm.top === "number") {
        _cachedPrintMargins = { top: pm.top, right: pm.right, bottom: pm.bottom, left: pm.left };
        if (active) setMargins(_cachedPrintMargins);
      }
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      JustifyTypography,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontSize,
      FontFamily,
      LineHeight,
      ParagraphSpacing,
      ParagraphIndent,
      TabIndent,
      Table.configure({ resizable: true, handleWidth: 5, cellMinWidth: 40 }),
      CustomTableRow,
      RowResize,
      CustomTableHeader,
      CustomTableCell,
      ResizableImage.configure({ inline: false, allowBase64: true }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
    ],
    content: value || "",
    editable: true,
    parseOptions: { preserveWhitespace: "full" },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: { class: "pgp-prose focus:outline-none", spellcheck: String(spellcheck) },
    },
  });

  // Keep editor content in sync when the loaded document changes (fixes formatting not restored on reopen)
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false, { preserveWhitespace: "full" });
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor && onEditorReady) onEditorReady(editor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    onMarginsChange?.(margins);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [margins]);

  const startMarginDrag = (edge, axis) => (e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const start = margins[edge];
    const scale = zoom / 100;
    const onMove = (ev) => {
      const dmm = axis === "x"
        ? (ev.clientX - startX) * MM_PER_PX / scale
        : (ev.clientY - startY) * MM_PER_PX / scale;
      let val = (edge === "left" || edge === "top") ? start + dmm : start - dmm;
      const limit = axis === "x" ? PAGE_W : PAGE_H;
      val = Math.max(0, Math.min(limit - 20, Math.round(val)));
      setMargins((m) => ({ ...m, [edge]: val }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const applyLineHeight = (v) => {
    if (!editor) return;
    const { from, empty } = editor.state.selection;
    let chain = editor.chain().focus();
    if (empty) chain = chain.selectAll();
    chain = chain.updateAttributes("paragraph", { lineHeight: v }).updateAttributes("heading", { lineHeight: v });
    if (empty) chain = chain.setTextSelection(from);
    chain.run();
  };

  const applyParaSpacing = (v) => {
    if (!editor) return;
    const { from, empty } = editor.state.selection;
    let chain = editor.chain().focus();
    if (empty) chain = chain.selectAll();
    chain = chain.updateAttributes("paragraph", { marginBottom: v });
    if (empty) chain = chain.setTextSelection(from);
    chain.run();
  };

  const applyFontSize = (v) => {
    if (!editor) return;
    const { from, empty } = editor.state.selection;
    let chain = editor.chain().focus();
    if (empty) chain = chain.selectAll();
    chain = chain.setMark("textStyle", { fontSize: v || null });
    if (empty) chain = chain.setTextSelection(from);
    chain.run();
  };

  // Misura sincrona: true se il contenuto va oltre la prima pagina A4.
  const overflowsNow = () => {
    const sheet = sheetRef.current;
    const last = editor?.view?.dom?.lastElementChild;
    if (!sheet || !last) return false;
    const sr = sheet.getBoundingClientRect();
    const scale = sheet.offsetWidth ? sr.width / sheet.offsetWidth : 1;
    return (last.getBoundingClientRect().bottom - sr.top) / (scale || 1) > (PAGE_H - margins.bottom) * (96 / 25.4) + 2;
  };

  // Riduce interlinea e poi, in proporzione, tutte le dimensioni del carattere finché il testo sta in un foglio.
  const fitToPage = () => {
    if (!editor || !overflowsNow()) return;
    const view = editor.view;
    const ts = editor.schema.marks.textStyle;
    const base = parseFloat(getComputedStyle(view.dom).fontSize) || 14;
    for (const lh of ["1"]) {
      const tr = view.state.tr;
      view.state.doc.descendants((n, pos) => {
        if ((n.type.name === "paragraph" || n.type.name === "heading") && parseFloat(n.attrs.lineHeight || DEFAULT_LINE_HEIGHT) > parseFloat(lh)) {
          tr.setNodeMarkup(pos, undefined, { ...n.attrs, lineHeight: lh, ...(n.attrs.marginBottom ? { marginBottom: null } : {}) });
        }
      });
      if (tr.docChanged) view.dispatch(tr);
      if (!overflowsNow()) { toast.success("Lettera adattata a una pagina (Ctrl+Z per annullare)"); return; }
    }
    for (let i = 0; i < 14 && overflowsNow(); i++) {
      const tr = view.state.tr;
      let minHit = true;
      view.state.doc.descendants((n, pos) => {
        if (!n.isText) return;
        const m = n.marks.find((x) => x.type === ts);
        const cur = parseFloat(m?.attrs?.fontSize) || base;
        const nx = Math.max(7, Math.round(cur * 0.94 * 10) / 10);
        if (nx < cur) minHit = false;
        tr.addMark(pos, pos + n.nodeSize, ts.create({ ...(m?.attrs || {}), fontSize: `${nx}px` }));
      });
      view.dispatch(tr);
      if (minHit) break;
    }
    if (overflowsNow()) toast.warning("Testo ridotto al minimo leggibile: la lettera non sta ancora in un foglio, accorcia il contenuto");
    else toast.success("Lettera adattata a una pagina (Ctrl+Z per annullare)");
  };

  const applyFontFamily = (v) => {
    if (!editor) return;
    const { from, empty } = editor.state.selection;
    let chain = editor.chain().focus();
    if (empty) chain = chain.selectAll();
    chain = chain.setMark("textStyle", { fontFamily: v || null });
    if (empty) chain = chain.setTextSelection(from);
    chain.run();
  };

  // Applica gli attributi bordo a TUTTE le celle della tabella corrente.
  const setTableBorders = (attrs) => {
    if (!editor) return;
    const { state } = editor;
    const { $anchor } = state.selection;
    let tableNode = null, tablePos = null;
    for (let d = $anchor.depth; d > 0; d--) {
      const n = $anchor.node(d);
      if (n.type.name === "table") { tableNode = n; tablePos = $anchor.before(d); break; }
    }
    if (!tableNode) return;
    const tr = state.tr;
    tableNode.descendants((node, pos) => {
      if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
        tr.setNodeMarkup(tablePos + 1 + pos, undefined, { ...node.attrs, ...attrs });
      }
    });
    editor.view.dispatch(tr);
    editor.commands.focus();
  };

  useEffect(() => {
    if (editor) editor.view.dom.setAttribute("spellcheck", String(spellcheck));
  }, [spellcheck, editor]);

  // Ancora la barra comandi tabella vicino alla cella selezionata, SEMPRE dentro il viewport.
  // Usa esclusivamente API ProseMirror same-origin (coordsAtPos): nessun accesso a nodi DOM
  // cross-origin. Ogni eventuale errore viene assorbito (fallback alla barra sticky in alto).
  useEffect(() => {
    if (!editor) return;
    const update = () => {
      try {
        if (!editor.isActive("table")) { setTableBar((p) => (p ? null : p)); return; }
        const { from } = editor.state.selection;
        const c = editor.view.coordsAtPos(from);
        const w = tableBarRef.current?.offsetWidth || 380;
        const h = tableBarRef.current?.offsetHeight || 42;
        const pad = 8;
        const left = Math.min(Math.max(pad, c.left - 8), window.innerWidth - w - pad);
        let top = c.top - h - pad;
        if (top < pad) top = Math.min(c.bottom + pad, window.innerHeight - h - pad);
        setTableBar({ top: Math.max(pad, top), left: Math.max(pad, left) });
      } catch { setTableBar((p) => (p ? null : p)); }
    };
    update();
    editor.on("selectionUpdate", update);
    editor.on("transaction", update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [editor]);

  // Barra comandi tabella: CHIUSA di default. Si apre SOLO col pulsante dedicato
  // nella toolbar; non si apre mai automaticamente cliccando dentro una tabella.
  // Quando la selezione esce dalla tabella la barra viene richiusa.
  useEffect(() => {
    if (!editor) return;
    const onSel = () => { if (!editor.isActive("table")) setShowTableBar(false); };
    editor.on("selectionUpdate", onSel);
    return () => { editor.off("selectionUpdate", onSel); };
  }, [editor]);

  // Avviso se il contenuto supera la prima pagina A4.
  useEffect(() => {
    if (!editor) return undefined;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const sheet = sheetRef.current;
        const last = editor.view?.dom?.lastElementChild;
        if (!sheet || !last) { setOverPages(0); return; }
        const sr = sheet.getBoundingClientRect();
        const scale = sheet.offsetWidth ? sr.width / sheet.offsetWidth : 1;
        const bottomPx = (last.getBoundingClientRect().bottom - sr.top) / (scale || 1);
        const pxPerMm = 96 / 25.4;
        const limit = (PAGE_H - margins.bottom) * pxPerMm;
        const pageUsable = (PAGE_H - margins.top - margins.bottom) * pxPerMm;
        setOverPages(bottomPx > limit + 2 ? 1 + Math.ceil((bottomPx - limit) / pageUsable) : 0);
      });
    };
    measure();
    editor.on("update", measure);
    const t = setTimeout(measure, 400);
    return () => { editor.off("update", measure); cancelAnimationFrame(raf); clearTimeout(t); };
  }, [editor, margins.top, margins.bottom, withHead, letterhead]);

  const selectAll = () => editor?.chain().focus().selectAll().run();

  const copySelection = async () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, "\n");
    if (text) {
      try { await navigator.clipboard.writeText(text); } catch (_) { /* clipboard blocked */ }
    }
  };

  const pasteClipboard = async () => {
    if (!editor) return;
    try {
      const text = await navigator.clipboard.readText();
      if (text) editor.chain().focus().insertContent(text.replace(/\n/g, "<br>")).run();
    } catch (_) { /* clipboard blocked */ }
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editor) return;
    const reader = new FileReader();
    reader.onload = () => editor.chain().focus().setImage({ src: reader.result }).run();
    reader.readAsDataURL(file);
  };

  const insertLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Inserisci l'URL del link:", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  // Cliccando in un punto qualsiasi del foglio (anche fuori dal testo) posiziona il cursore
  // nell'editor, così l'utente può sempre scrivere cliccando nel documento.
  const focusEditorFromSheet = (e) => {
    if (!editor) return;
    try {
      const pm = e.currentTarget.querySelector(".ProseMirror");
      if (pm && !pm.contains(e.target)) {
        e.preventDefault();
        editor.commands.focus("end");
      }
    } catch { /* target cross-origin: ignora */ }
  };

  if (!editor) return null;

  const cmH = Array.from({ length: PAGE_W / 10 + 1 }, (_, i) => i);
  const cmV = Array.from({ length: Math.floor(PAGE_H / 10) + 1 }, (_, i) => i);
  const curLineHeight = editor.getAttributes("paragraph").lineHeight || DEFAULT_LINE_HEIGHT;
  const curParaSpacing = editor.getAttributes("paragraph").marginBottom || "0";
  const curFontSize = editor.getAttributes("textStyle").fontSize || "";
  const curFontFamily = editor.getAttributes("textStyle").fontFamily || "";

  return (
    <div className={`rounded-lg border border-slate-700 bg-slate-900/40 overflow-hidden ${tableGuide ? "pgp-guide-on" : ""}`} data-testid="rich-text-editor">
      {editor.isActive("table") && showTableBar && (
        <div
          ref={tableBarRef}
          data-testid="table-command-bar"
          style={tableBar
            ? { position: "fixed", top: tableBar.top, left: tableBar.left, zIndex: 60 }
            : { position: "sticky", top: 0, zIndex: 30 }}
          className="flex flex-wrap items-center gap-0.5 rounded-md border border-slate-600 bg-[#121824] px-2 py-1.5 shadow-xl max-w-[95vw]">
          <span className="text-[10px] uppercase tracking-wider text-amber-400 font-mono mr-1.5 pr-1.5 border-r border-slate-700 flex items-center gap-1"><LayoutGrid className="h-3.5 w-3.5" /> Tabella</span>
          <TblBtn onClick={() => editor.chain().focus().addColumnBefore().run()} title="Aggiungi colonna prima" testId="tbl-col-before"><BetweenVerticalStart className="h-4 w-4" /></TblBtn>
          <TblBtn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Aggiungi colonna dopo" testId="tbl-col-after"><BetweenVerticalEnd className="h-4 w-4" /></TblBtn>
          <TblBtn onClick={() => editor.chain().focus().deleteColumn().run()} title="Elimina colonna" testId="tbl-col-del"><Minus className="h-4 w-4" /><span className="text-[10px] font-bold ml-0.5">col</span></TblBtn>
          <span className="mx-1 h-5 w-px bg-slate-700" />
          <TblBtn onClick={() => editor.chain().focus().addRowBefore().run()} title="Aggiungi riga sopra" testId="tbl-row-before"><BetweenHorizontalStart className="h-4 w-4" /></TblBtn>
          <TblBtn onClick={() => editor.chain().focus().addRowAfter().run()} title="Aggiungi riga sotto" testId="tbl-row-after"><BetweenHorizontalEnd className="h-4 w-4" /></TblBtn>
          <TblBtn onClick={() => editor.chain().focus().deleteRow().run()} title="Elimina riga" testId="tbl-row-del"><Minus className="h-4 w-4" /><span className="text-[10px] font-bold ml-0.5">riga</span></TblBtn>
          <span className="mx-1 h-5 w-px bg-slate-700" />
          <TblBtn onClick={() => editor.chain().focus().mergeCells().run()} title="Unisci celle" testId="tbl-merge"><Combine className="h-4 w-4" /></TblBtn>
          <TblBtn onClick={() => editor.chain().focus().splitCell().run()} title="Dividi cella" testId="tbl-split"><Split className="h-4 w-4" /></TblBtn>
          <span className="mx-1 h-5 w-px bg-slate-700" />
          <TableBorderControl editor={editor} onApply={setTableBorders} />
          <CellBgControl editor={editor} />
          <span className="mx-1 h-5 w-px bg-slate-700" />
          <TblBtn onClick={() => editor.chain().focus().setCellAttribute("verticalAlign", "top").run()} active={editor.getAttributes("tableCell").verticalAlign === "top"} title="Allinea contenuto in alto" testId="tbl-valign-top"><AlignVerticalJustifyStart className="h-4 w-4" /></TblBtn>
          <TblBtn onClick={() => editor.chain().focus().setCellAttribute("verticalAlign", "middle").run()} active={editor.getAttributes("tableCell").verticalAlign === "middle"} title="Allinea contenuto al centro" testId="tbl-valign-middle"><AlignVerticalJustifyCenter className="h-4 w-4" /></TblBtn>
          <TblBtn onClick={() => editor.chain().focus().setCellAttribute("verticalAlign", "bottom").run()} active={editor.getAttributes("tableCell").verticalAlign === "bottom"} title="Allinea contenuto in basso" testId="tbl-valign-bottom"><AlignVerticalJustifyEnd className="h-4 w-4" /></TblBtn>
          <TblBtn onClick={() => setTableGuide((g) => !g)} active={tableGuide} title="Mostra guida (bordo tratteggiato solo in editor)" testId="tbl-guide"><LayoutGrid className="h-4 w-4" /></TblBtn>
          <span className="mx-1 h-5 w-px bg-slate-700" />
          <TblBtn onClick={() => editor.chain().focus().deleteTable().run()} title="Elimina tabella" testId="tbl-delete" danger><Trash2 className="h-4 w-4" /></TblBtn>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-800 px-2 py-1.5 bg-slate-900/60">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Grassetto" testId="rte-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Corsivo" testId="rte-italic"><Italic className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Sottolineato" testId="rte-underline"><UnderlineIcon className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-slate-700" />
        <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Allinea a sinistra" testId="rte-left"><AlignLeft className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centra" testId="rte-center"><AlignCenter className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Allinea a destra" testId="rte-right"><AlignRight className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Giustifica" testId="rte-justify"><AlignJustify className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-slate-700" />
        <Btn onClick={() => editor.chain().focus().changeIndent(-INDENT_STEP_CM).run()} title="Riduci rientro" testId="rte-indent-dec"><IndentDecrease className="h-4 w-4" /></Btn>
        <span className="text-[10px] font-mono text-slate-400 w-9 text-center tabular-nums" title="Rientro sinistro del paragrafo" data-testid="rte-indent-value">{(editor.getAttributes("paragraph").indentLeft || editor.getAttributes("heading").indentLeft || 0).toString().replace(".", ",")}cm</span>
        <Btn onClick={() => editor.chain().focus().changeIndent(INDENT_STEP_CM).run()} title="Aumenta rientro (tutte le righe del paragrafo)" testId="rte-indent-inc"><IndentIncrease className="h-4 w-4" /></Btn>
        <select
          value={String(editor.getAttributes("paragraph").firstIndent || editor.getAttributes("heading").firstIndent || 0)}
          onChange={(e) => editor.chain().focus().setFirstIndent(parseFloat(e.target.value)).run()}
          data-testid="rte-first-indent"
          title="Rientro prima riga"
          className="h-8 rounded-md bg-slate-700 text-white text-xs border border-slate-500 px-1.5 focus:outline-none focus:border-amber-500 [&_option]:bg-white [&_option]:text-slate-900"
        >
          {FIRST_INDENTS.map((v) => <option key={v} value={String(v)} label={v ? `↦ 1ª riga ${String(v).replace(".", ",")} cm` : "↦ 1ª riga: no"} />)}
        </select>
        <span className="mx-1 h-5 w-px bg-slate-700" />
        <ColorPopover title="Colore testo" testId="rte-color" icon={<Baseline className="h-4 w-4" />} colors={TEXT_COLORS}
          onPick={(c) => editor.chain().focus().setColor(c).run()} onClear={() => editor.chain().focus().unsetColor().run()} />
        <ColorPopover title="Colore sfondo testo (evidenziatore)" testId="rte-highlight" icon={<Highlighter className="h-4 w-4" />} colors={HIGHLIGHT_COLORS}
          onPick={(c) => editor.chain().focus().setHighlight({ color: c }).run()} onClear={() => editor.chain().focus().unsetHighlight().run()} />
        <span className="mx-1 h-5 w-px bg-slate-700" />
        <select
          value={curLineHeight}
          onChange={(e) => applyLineHeight(e.target.value)}
          data-testid="rte-line-height"
          title="Interlinea"
          className="h-8 rounded-md bg-slate-700 text-white text-xs border border-slate-500 px-1.5 focus:outline-none focus:border-amber-500 [&_option]:bg-white [&_option]:text-slate-900"
        >
          {LINE_HEIGHTS.map((o) => <option key={o.v} value={o.v} label={`↕ ${o.l}`} />)}
        </select>
        <select
          value={curParaSpacing}
          onChange={(e) => applyParaSpacing(e.target.value)}
          data-testid="rte-para-spacing"
          title="Spazio paragrafo (sotto)"
          className="h-8 rounded-md bg-slate-700 text-white text-xs border border-slate-500 px-1.5 focus:outline-none focus:border-amber-500 [&_option]:bg-white [&_option]:text-slate-900"
        >
          {PARA_SPACINGS.map((o) => <option key={o.v} value={o.v} label={`¶ ${o.l}`} />)}
        </select>
        <select
          value={curFontFamily}
          onChange={(e) => applyFontFamily(e.target.value)}
          data-testid="rte-font-family"
          title="Tipo di carattere"
          className="h-8 rounded-md bg-slate-700 text-white text-xs border border-slate-500 px-1.5 focus:outline-none focus:border-amber-500 [&_option]:bg-white [&_option]:text-slate-900"
        >
          <option value="">Carattere</option>
          {FONT_FAMILIES.map((f) => <option key={f.l} value={f.v} style={{ fontFamily: f.v }}>{f.l}</option>)}
        </select>
        <select
          value={curFontSize}
          onChange={(e) => applyFontSize(e.target.value)}
          data-testid="rte-font-size"
          title="Dimensione carattere"
          className="h-8 rounded-md bg-slate-700 text-white text-xs border border-slate-500 px-1.5 focus:outline-none focus:border-amber-500 [&_option]:bg-white [&_option]:text-slate-900"
        >
          <option value="">Font</option>
          {FONT_SIZES.map((s) => <option key={s} value={s}>{s.replace("px", "")}</option>)}
        </select>
        <span className="mx-1 h-5 w-px bg-slate-700" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Elenco puntato" testId="rte-bullet"><List className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Elenco numerato" testId="rte-ordered"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Inserisci tabella" testId="rte-table"><TableIcon className="h-4 w-4" /></Btn>
        <Btn onClick={() => setShowTableBar((s) => !s)} active={showTableBar} disabled={!editor.isActive("table")} title={!editor.isActive("table") ? "Barra comandi tabella (posizionati in una tabella)" : showTableBar ? "Nascondi barra comandi tabella" : "Mostra barra comandi tabella"} testId="rte-toggle-table-bar"><Grid3x3 className="h-4 w-4" /></Btn>
        <Btn onClick={() => fileInputRef.current?.click()} title="Inserisci immagine" testId="rte-image"><ImagePlus className="h-4 w-4" /></Btn>
        <Btn onClick={insertLink} active={editor.isActive("link")} title="Inserisci link" testId="rte-link"><LinkIcon className="h-4 w-4" /></Btn>
        {Array.isArray(destinatariCompagnie) && (
          <>
            <span className="mx-1 h-5 w-px bg-slate-700" />
            <DestinatarioPraticaControl editor={editor} companies={destinatariCompagnie} />
          </>
        )}
        <span className="mx-1 h-5 w-px bg-slate-700" />
        <Btn onClick={selectAll} title="Seleziona tutto" testId="rte-select-all"><TextSelect className="h-4 w-4" /></Btn>
        <Btn onClick={copySelection} title="Copia selezione" testId="rte-copy"><Copy className="h-4 w-4" /></Btn>
        <Btn onClick={pasteClipboard} title="Incolla" testId="rte-paste"><ClipboardPaste className="h-4 w-4" /></Btn>
        <Btn onClick={() => setSpellcheck((s) => !s)} active={spellcheck} title="Controllo ortografico" testId="rte-spellcheck"><SpellCheck className="h-4 w-4" /></Btn>
        <FindReplace editor={editor} />
        <span className="mx-1 h-5 w-px bg-slate-700" />
        <Btn onClick={() => editor.chain().focus().undo().run()} title="Annulla" testId="rte-undo"><Undo className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} title="Ripeti" testId="rte-redo"><Redo className="h-4 w-4" /></Btn>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickImage} data-testid="rte-image-input" className="hidden" />
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 px-3 py-1.5 bg-slate-900/60" data-testid="rte-zoom-bar">
        {overPages > 1 && (
          <div className="basis-full flex items-center gap-2 rounded-md border border-amber-500/60 bg-amber-500/15 px-3 py-1.5 text-xs font-semibold text-amber-300" data-testid="rte-overflow-warning" role="alert">
            <AlertTriangle className="h-4 w-4 shrink-0" /> <span className="flex-1">Attenzione: il testo supera la prima pagina A4 (circa {overPages} pagine). Riduci il contenuto, il carattere o l'interlinea se la lettera deve stare in un solo foglio.</span>
            <button type="button" onClick={fitToPage} data-testid="rte-fit-page" className="shrink-0 rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-bold text-[#0a0d14] hover:bg-amber-400 transition-colors">Adatta a una pagina</button>
          </div>
        )}
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mr-1">Anteprima A4</span>
        <button type="button" onClick={() => { setAutoFit(false); setZoom((z) => Math.max(50, z - 10)); }} data-testid="rte-zoom-out" title="Riduci" className="h-7 w-7 flex items-center justify-center rounded-md text-slate-300 hover:text-amber-400 hover:bg-slate-700/50 transition-colors"><Minus className="h-4 w-4" /></button>
        <span data-testid="rte-zoom-value" className="text-xs font-mono text-slate-200 w-12 text-center tabular-nums">{zoom}%</span>
        <button type="button" onClick={() => { setAutoFit(false); setZoom((z) => Math.min(200, z + 10)); }} data-testid="rte-zoom-in" title="Ingrandisci" className="h-7 w-7 flex items-center justify-center rounded-md text-slate-300 hover:text-amber-400 hover:bg-slate-700/50 transition-colors"><Plus className="h-4 w-4" /></button>
        <button type="button" onClick={() => { setAutoFit(false); setZoom(100); }} data-testid="rte-zoom-reset" title="Reimposta 100%" className="text-[11px] font-mono rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-slate-300 hover:text-amber-400 hover:border-amber-500/40 transition-colors">100%</button>
        <button type="button" onClick={() => setAutoFit(true)} data-testid="rte-zoom-fit" title="Adatta alla larghezza" className={`text-[11px] font-mono rounded border px-2 py-0.5 transition-colors ${autoFit ? "border-amber-500/60 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/60 text-slate-300 hover:text-amber-400 hover:border-amber-500/40"}`}>Adatta</button>
        <span className="mx-1 h-4 w-px bg-slate-700" />
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Margini mm</span>
        <span data-testid="rte-margins-value" className="text-[11px] font-mono text-slate-300 tabular-nums">{margins.top}·{margins.right}·{margins.bottom}·{margins.left}</span>
        <button type="button" onClick={() => setMargins(DEFAULT_MARGINS)} data-testid="rte-margins-reset" title="Reimposta margini" className="text-[11px] font-mono rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-slate-300 hover:text-amber-400 hover:border-amber-500/40 transition-colors">Reset</button>
      </div>

      <div className="pgp-a4-canvas" data-testid="rte-a4-canvas" ref={canvasRef} style={canvasMaxH ? { maxHeight: `${canvasMaxH}px`, height: `${canvasMaxH}px`, padding: "12px" } : undefined}>
        <div className="pgp-a4-outer" style={{ transform: `scale(${zoom / 100})`, transformOrigin: "top center" }}>
          {/* horizontal ruler */}
          <div className="pgp-ruler-h" data-testid="rte-ruler-h">
            {cmH.map((c) => (
              <span key={c}>
                <span className="pgp-tick pgp-tick-h major" style={{ left: `${c * 10}mm` }} />
                {c > 0 && c < PAGE_W / 10 && <span className="pgp-tick-label-h" style={{ left: `${c * 10}mm` }}>{c}</span>}
              </span>
            ))}
            <span className="pgp-mh" style={{ left: `${margins.left}mm` }} onMouseDown={startMarginDrag("left", "x")} data-testid="rte-margin-left" title="Margine sinistro" />
            <span className="pgp-mh" style={{ left: `${PAGE_W - margins.right}mm` }} onMouseDown={startMarginDrag("right", "x")} data-testid="rte-margin-right" title="Margine destro" />
          </div>

          <div className="pgp-a4-body">
            {/* vertical ruler */}
            <div className="pgp-ruler-v" data-testid="rte-ruler-v">
              {cmV.map((c) => (
                <span key={c}>
                  <span className="pgp-tick pgp-tick-v major" style={{ top: `${c * 10}mm` }} />
                  {c > 0 && c < PAGE_H / 10 && <span className="pgp-tick-label-v" style={{ top: `${c * 10}mm` }}>{c}</span>}
                </span>
              ))}
              <span className="pgp-mv" style={{ top: `${margins.top}mm` }} onMouseDown={startMarginDrag("top", "y")} data-testid="rte-margin-top" title="Margine superiore" />
              <span className="pgp-mv" style={{ top: `${PAGE_H - margins.bottom}mm` }} onMouseDown={startMarginDrag("bottom", "y")} data-testid="rte-margin-bottom" title="Margine inferiore" />
            </div>

            <div
              ref={sheetRef}
              className="pgp-a4-sheet"
              data-overflow={overPages > 1 ? "1" : "0"}
              style={{ padding: `${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm` }}
              data-testid="rte-a4-sheet"
              onMouseDown={focusEditorFromSheet}
            >
              <div
                className="pgp-margin-guide"
                style={{ top: `${margins.top}mm`, right: `${margins.right}mm`, bottom: `${margins.bottom}mm`, left: `${margins.left}mm` }}
                aria-hidden
              />
              <div className="pgp-page-breaks" aria-hidden />
              {withHead && letterhead && (
                letterhead.html ? <div className="pgp-letterhead" data-testid="rte-letterhead" dangerouslySetInnerHTML={{ __html: letterhead.html }} /> :
                <div className="pgp-letterhead" data-testid="rte-letterhead" style={{ textAlign: ["left", "center", "right"].includes(letterhead.align) ? letterhead.align : "left" }}>
                  <div className="lh-name">{letterhead.nome}</div>
                  {(letterhead.lines || []).map((ln, i) => (
                    <div key={i} className="lh-line">{ln}</div>
                  ))}
                </div>
              )}
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
